import * as CL from '@design-system/component-library';
import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { dsClass } from '../../common/constants/dsClasses.js';
import { migrateUsers } from '../../common/fetch.js';
import {
  confirmMsg,
  greetingNoNameMsg,
  hiPleaseVerifyWorkContactDetailsMsg,
  logOutMsg,
  retryMsg,
  t,
} from '../../common/i18n/index.js';
import { Email, PhoneNumber } from '../../common/react-hook-form/fields/index.js';
import { useAuth } from '../../public/site/AuthProvider.js';
import { removeMigrationSkipped } from '../../selfservice/common/localStorageUtils.js';
import { Modal } from '../Modal/Modal.js';
import { ElisaIdMigrationConflictModal } from './ElisaIdMigrationConflictModal.js';

import './ElisaIdMigrationVerifyContact.scss';

export interface ElisaIdMigrationVerifyContactProps {
  onSuccess: (email: string) => void;
  errorState?: MigrationErrorState;
}

type MigrationErrorState = 'none' | 'genericRetryable' | 'accountConflict';

const migrationRetryableErrorMessage = 'Error with migration, please try again or log out.';

export const ElisaIdMigrationVerifyContact = ({
  onSuccess,
  errorState: errorStateOverride,
}: ElisaIdMigrationVerifyContactProps) => {
  const { authenticatedUser, validateAuth, logout } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [errorState, setErrorState] = useState<MigrationErrorState>('none');
  const effectiveErrorState = errorStateOverride ?? errorState;

  const methods = useForm({
    mode: 'all',
    defaultValues: {
      email: authenticatedUser?.email ?? '',
      phoneNumber: authenticatedUser?.mobile ?? '',
    },
  });

  const { isValid } = methods.formState;

  const handleSubmit = methods.handleSubmit(async ({ email, phoneNumber }) => {
    setErrorState('none');
    setIsLoading(true);
    try {
      const response = await migrateUsers({ email, mobileNumber: phoneNumber });
      removeMigrationSkipped();
      validateAuth();
      onSuccess(response.userID);
    } catch (error) {
      const statusCode = error instanceof Response ? error.status : undefined;
      if (statusCode === 409) {
        setErrorState('accountConflict');
      } else {
        setErrorState('genericRetryable');
      }
      setIsLoading(false);
    }
  });

  if (effectiveErrorState === 'accountConflict') {
    return <ElisaIdMigrationConflictModal onLogout={() => logout()} />;
  }

  return (
    <Modal size="m" className="of-elisa-id-migration-verify-contact__dialog">
      <div className={`of-elisa-id-migration-verify-contact ${dsClass.DISPLAY_FLEX}`}>
        {effectiveErrorState === 'genericRetryable' && (
          <CL.Notification hasCloseButton={false} text={t.JTEX(migrationRetryableErrorMessage)} type="error" visible />
        )}
        <h3 className={`${dsClass.MARGIN_0}`}>
          {`${t.UC34(greetingNoNameMsg)} ${authenticatedUser?.firstName ?? ''},`} <br />
          {t.NRMH(hiPleaseVerifyWorkContactDetailsMsg)}
        </h3>
        <FormProvider {...methods}>
          <form noValidate onSubmit={handleSubmit}>
            <Email />
            <PhoneNumber />
            {effectiveErrorState === 'genericRetryable' ? (
              <div className="of-elisa-id-migration-verify-contact__actions">
                <CL.Button color="secondary" size="l" type="button" onClick={() => logout()}>
                  {t.LQ3X(logOutMsg)}
                </CL.Button>
                <CL.Button color="primary" size="l" type="submit" disabled={!isValid || isLoading} loading={isLoading}>
                  {t.NP6Z(retryMsg)}
                </CL.Button>
              </div>
            ) : (
              <CL.Button
                color="primary"
                size="l"
                block
                type="submit"
                disabled={!isValid || isLoading}
                loading={isLoading}
              >
                {t.QVYK(confirmMsg)}
              </CL.Button>
            )}
          </form>
        </FormProvider>
      </div>
    </Modal>
  );
};
